import { tokens } from './client';
import type { CallLog } from './types';

/**
 * Subscribes to live call status.
 *
 * Uses fetch with a streaming body rather than EventSource. EventSource cannot
 * set request headers, so the access token would have to go in the query
 * string — where it lands in nginx's access log and in browser history. A
 * streamed fetch carries the normal Authorization header.
 *
 * Returns an unsubscribe. Reconnects with backoff, because the connection is
 * expected to drop: idle proxies close it, and the backend closes every stream
 * on deploy.
 */
export function subscribeToCalls(onCall: (call: CallLog) => void): () => void {
  const controller = new AbortController();
  let stopped = false;
  let retryMs = 1_000;

  async function connect(): Promise<void> {
    while (!stopped) {
      try {
        const res = await fetch('/api/v1/calls/stream/events', {
          headers: { Authorization: `Bearer ${tokens.access ?? ''}`, Accept: 'text/event-stream' },
          signal: controller.signal,
credentials: 'same-origin',
        });

        if (!res.ok || !res.body) throw new Error(`stream failed: ${res.status}`);

        retryMs = 1_000;
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          // SSE frames are separated by a blank line. A single read can hold
          // several, or half of one.
          let split = buffer.indexOf('\n\n');
          while (split !== -1) {
            const frame = buffer.slice(0, split);
            buffer = buffer.slice(split + 2);
            handleFrame(frame, onCall);
            split = buffer.indexOf('\n\n');
          }
        }
      } catch (err) {
        if (stopped || controller.signal.aborted) return;
        // Fall through to the backoff below; a failed connect and a dropped
        // connection are the same problem from here.
      }

      if (stopped) return;
      await new Promise((resolve) => setTimeout(resolve, retryMs));
      retryMs = Math.min(retryMs * 2, 30_000);
    }
  }

  void connect();

  return () => {
    stopped = true;
    controller.abort();
  };
}

function handleFrame(frame: string, onCall: (call: CallLog) => void): void {
  // Comment frames (": ping") keep the connection warm and carry no data.
  const dataLines = frame
    .split('\n')
    .filter((line) => line.startsWith('data:'))
    .map((line) => line.slice(5).trim());

  if (dataLines.length === 0) return;

  try {
    onCall(JSON.parse(dataLines.join('\n')) as CallLog);
  } catch {
    // A malformed frame must not kill the stream.
  }
}
